import { useEffect, useState } from "react";
import { useSiteSettings } from "@/hooks/content";
import { supabase } from "@/lib/supabase";
import { PageHeader, useToast } from "./components/admin-ui";

export default function SiteSettingsPage() {
  const { record, loading, error, update } = useSiteSettings();
  const { show } = useToast();

  const [form, setForm] = useState(() => ({ ...record }));
  const [dirty, setDirty] = useState(false);
  const [synced, setSynced] = useState(false);

  const [testEmail, setTestEmail] = useState(record.email || "");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  // Keep the form in sync once the record loads from the backend.
  useEffect(() => {
    setForm({ ...record });
    setTestEmail(record.email || "");
    setDirty(false);
  }, [record]);

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
    setSynced(false);
  }

  async function save() {
    await update(form);
    setDirty(false);
    setSynced(true);
    show("Website settings saved.");
  }

  async function sendTestEmail() {
    const to = testEmail.trim();
    if (!to || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) {
      setTestResult({ ok: false, message: "Please enter a valid recipient email address." });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("send-test-email", {
        body: { to },
      });
      if (fnError) throw fnError;
      if (data?.ok) {
        setTestResult({ ok: true, message: data.message || "Test email sent." });
      } else {
        setTestResult({ ok: false, message: data?.error || "Test email failed." });
      }
    } catch (e) {
      setTestResult({
        ok: false,
        message: e instanceof Error ? e.message : "Could not send test email.",
      });
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Website Settings"
        subtitle="Manage branding and contact details shown across the website, footer and contact page."
        actions={
          <button
            type="button"
            onClick={save}
            disabled={!dirty}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-save-line text-sm" />
            </span>
            Save Changes
          </button>
        }
      />

      {loading ? (
        <div className="bg-white rounded-lg border border-background-200 p-10 text-center text-sm text-foreground-500">
          Loading settings…
        </div>
      ) : error ? (
        <div className="bg-accent-50 border border-accent-200 rounded-lg p-6 text-sm text-accent-700">
          {error}
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg border border-background-200 p-5">
            <h2 className="text-sm font-semibold text-foreground-900 mb-4">Branding & contact</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">Website name</label>
                <input
                  type="text"
                  value={form.siteName}
                  onChange={(e) => set("siteName", e.target.value)}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">Tagline</label>
                <input
                  type="text"
                  value={form.tagline}
                  onChange={(e) => set("tagline", e.target.value)}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="contact@ltvtelevision.com"
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">Phone</label>
                <input
                  type="text"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="+237 …"
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">WhatsApp number</label>
                <input
                  type="text"
                  value={form.whatsapp}
                  onChange={(e) => set("whatsapp", e.target.value)}
                  placeholder="+237 6XX XX XX XX"
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">Address</label>
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => set("address", e.target.value)}
                  placeholder="Edea, Cameroon"
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">P.O. Box</label>
                <input
                  type="text"
                  value={form.poBox}
                  onChange={(e) => set("poBox", e.target.value)}
                  placeholder="EDEA - Cameroon"
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">Copyright</label>
                <input
                  type="text"
                  value={form.copyright}
                  onChange={(e) => set("copyright", e.target.value)}
                  placeholder="© {year} L+TV TÉLÉVISION"
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
            </div>
            {synced && (
              <p className="mt-4 text-xs text-green-600 flex items-center gap-1.5">
                <i className="ri-checkbox-circle-line text-sm" /> Changes saved to the database.
              </p>
            )}
          </div>

          <div className="bg-white rounded-lg border border-background-200 p-5">
            <h2 className="text-sm font-semibold text-foreground-900 mb-1">Test email sending</h2>
            <p className="text-xs text-foreground-500 mb-4">
              Send a test email to confirm your Resend sending domain is configured. Requires{" "}
              <span className="font-medium">RESEND_API_KEY</span> and{" "}
              <span className="font-medium">RESEND_FROM_DOMAIN</span> in your Supabase Dashboard.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="recipient@example.com"
                className="flex-1 px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
              <button
                type="button"
                onClick={sendTestEmail}
                disabled={testing}
                className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap"
              >
                {testing ? (
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-mail-send-line text-sm" />
                  </span>
                )}
                {testing ? "Sending…" : "Send Test Email"}
              </button>
            </div>
            {testResult && (
              <div
                className={`mt-4 text-xs rounded-md p-3 flex items-start gap-2 ${
                  testResult.ok
                    ? "bg-green-50 border border-green-200 text-green-700"
                    : "bg-accent-50 border border-accent-200 text-accent-700"
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                  <i className={`${testResult.ok ? "ri-checkbox-circle-line" : "ri-error-warning-line"} text-sm`} />
                </span>
                <span>{testResult.message}</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}